import { PAT_KEY_VALUE } from 'modules/Apps/mstStores/UserKeysMST';
import { useCallback, useMemo, useState } from 'react';
import { PatOption } from 'modules/Compute/Components/ClusterPAT/PatOption';
import { apiListKeys, createKeyTE, TcreateKeyParams } from 'api/keys';
import { displayError } from 'api/apiUtils';
import { either, errorToReactLeft } from 'utils/fp';
import { useQuery, useQueryClient } from 'react-query';
import { queryKeys } from 'utils/react-query';
import { Clickable } from 'reakit';
import { newPatStyle } from './SetupComputeModal.styles';

export type UsePATOptionsProps = {
  /**
   * The user id to fetch the PATs for.
   */
  userId: string;
  /**
   * Whether to select the default PAT. This default PAT
   * is created by BE at the time of account creation.
   * @default true
   */
  selectDefaultPAT?: boolean;
  /**
   * A function to optionally filter the PATs.
   */
  filterPAT?: (pat: CF.API.ApiKeys.KeyData) => boolean;
  /**
   * Used in the description of the new PAT when getPAT() is called.
   */
  newPatDescription?: string;
  /**
   * Callback to be called when the user clicks on "Create a new PAT".
   * Use this when you want to show a modal to create a new PAT instead
   * of calling createNewPAT().
   */
  onCreateNewPATClick?: () => void;
  /**
   * Hides the "Create a new PAT" option.
   * @default false
   */
  hideNewPatOption?: boolean;
};

export type UsePATOptionsReturn = {
  /**
   * The list of PAT options to be used in <Select /> component.
   */
  patOptions: {
    value: string;
    name: string;
    content: JSX.Element;
  }[];
  /**
   * Use in <Select /> component;
   */
  selectedPATOption?: string;
  /**
   * Use in <Select /> component;
   */
  setSelectedPatOption: (props: UsePATOptionsReturn['selectedPATOption']) => void;
  /**
   * Either call this function to create a new PAT later on inside of or use the
   * callback provided in `onCreateNewPATClick`.
   */
  createNewPAT: () => Promise<string | null | undefined>;
  /**
   * Whether the PATs are still loading.
   */
  isLoading: boolean;
};

export function getNewPATOption(onClick?: React.HTMLAttributes<HTMLDivElement>['onClick']) {
  return {
    value: 'Create a new PAT',
    name: 'Create a new PAT',
    content: (
      <Clickable as="div" className={newPatStyle} onClick={onClick}>
        Create a new <span className="pat-text">Personal Access Token</span>
      </Clickable>
    ),
  };
}

export const usePATOptions = ({
  userId,
  selectDefaultPAT = true,
  filterPAT,
  hideNewPatOption = false,
  newPatDescription,
  onCreateNewPATClick,
}: UsePATOptionsProps): UsePATOptionsReturn => {
  const queryClient = useQueryClient();
  const [selectedPATOption, _setSelectedPatOption] = useState<string>();
  const NEW_PAT_OPTION = getNewPATOption(onCreateNewPATClick);

  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.ListPATs, { userId }],
    queryFn: () => apiListKeys({}),
    staleTime: 1000 * 60, // 1 minute
    enabled: !!userId,
  });

  const setSelectedPatOption = useCallback(
    (pat?: string) => {
      // If the user clicks on "Create a new PAT" and the callback is provided,
      // then don't set the PAT as "Create a new PAT".
      if (pat === NEW_PAT_OPTION.value && onCreateNewPATClick) {
        return;
      }

      _setSelectedPatOption(pat);
    },
    [onCreateNewPATClick, _setSelectedPatOption],
  );

  /**
   * @returns The id of the newly created PAT.
   */
  const createNewPAT = async () => {
    if (NEW_PAT_OPTION.value === selectedPATOption) {
      const createKeyResponse = await createKeyTE(
        {
          type: PAT_KEY_VALUE,
          userId: userId as string,
          scopes: ['All'],
          description: newPatDescription,
        } as TcreateKeyParams,
        errorToReactLeft,
      )();

      if (either.isLeft(createKeyResponse)) {
        displayError(createKeyResponse.left.props?.reason);
        return null;
      }
      queryClient.invalidateQueries([queryKeys.ListPATs, { userId }]);
      return createKeyResponse.right.keys?.[0]?.id;
    }
    return selectedPATOption;
  };

  const patOptions = useMemo(() => {
    const userKeys = data?.keys || [];
    const filteredUserKeys = filterPAT ? userKeys.filter(filterPAT) : userKeys;
    const options = filteredUserKeys.map((pat) => ({
      value: pat.id,
      name: `PAT: ${pat.id}`,
      content: <PatOption key={pat.id} pat={pat} />,
    }));

    const defaultPat = filteredUserKeys.find((pat) => pat.description === 'default-all-scopes') ?? filteredUserKeys[0];
    if (selectDefaultPAT && defaultPat && !selectedPATOption) {
      setSelectedPatOption(defaultPat.id);
    }

    return hideNewPatOption ? options : [NEW_PAT_OPTION, ...options];
  }, [data?.keys?.length, selectedPATOption, selectDefaultPAT, filterPAT]);

  return { patOptions, selectedPATOption, setSelectedPatOption, createNewPAT, isLoading };
};
